import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Users, UserPlus, Crown, Trash2 } from 'lucide-react';

interface InternalTeamListProps {
  departmentName: 'Sales' | 'Marketing' | 'IT' | 'HR' | 'Accounting';
  canManage?: boolean;
}

export function InternalTeamList({ departmentName, canManage = false }: InternalTeamListProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');

  const { data: department } = useQuery({
    queryKey: ['internal-department', departmentName],
    queryFn: async () => {
      const { data, error } = await supabase.from('internal_departments').select('*').eq('name', departmentName).single();
      if (error) throw error;
      return data;
    },
  });

  const { data: teams = [] } = useQuery({
    queryKey: ['internal-teams', department?.id],
    queryFn: async () => {
      if (!department) return [];
      const { data, error } = await supabase.from('internal_teams').select('*').eq('department_id', department.id).order('created_at');
      if (error) throw error;
      return data || [];
    },
    enabled: !!department,
  });

  const { data: allMembers = [] } = useQuery({
    queryKey: ['internal-team-members', department?.id],
    queryFn: async () => {
      if (!teams.length) return [];
      const teamIds = teams.map((t: any) => t.id);
      const { data, error } = await supabase.from('internal_team_members').select('*').in('team_id', teamIds);
      if (error) throw error;
      return data || [];
    },
    enabled: teams.length > 0,
  });

  const { data: profiles = [] } = useQuery({
    queryKey: ['member-profiles', allMembers],
    queryFn: async () => {
      const userIds = [...new Set([...allMembers.map((m: any) => m.user_id), ...teams.map((t: any) => t.team_leader_id).filter(Boolean)])];
      if (!userIds.length) return [];
      const { data, error } = await supabase.from('profiles').select('user_id, full_name, avatar_url').in('user_id', userIds);
      if (error) throw error;
      return data || [];
    },
    enabled: allMembers.length > 0 || teams.some((t: any) => t.team_leader_id),
  });

  const getProfile = (userId: string) => profiles.find((p: any) => p.user_id === userId);

  const createTeam = useMutation({
    mutationFn: async () => {
      if (!department || !user) return;
      const { error } = await supabase.from('internal_teams').insert({
        department_id: department.id,
        name: newTeamName,
        created_by: user.id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['internal-teams'] });
      setShowCreate(false);
      setNewTeamName('');
      toast.success('Team created');
    },
    onError: () => toast.error('Failed to create team'),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{departmentName} Teams</h3>
        {canManage && (
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button size="sm"><Plus className="h-4 w-4 mr-1" /> Create Team</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create New Team</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input placeholder="Team name" value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} />
                <Button onClick={() => createTeam.mutate()} disabled={!newTeamName || createTeam.isPending} className="w-full">
                  {createTeam.isPending ? 'Creating...' : 'Create Team'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {teams.map((team: any) => {
          const members = allMembers.filter((m: any) => m.team_id === team.id);
          const leader = team.team_leader_id ? getProfile(team.team_leader_id) : null;
          return (
            <Card key={team.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="h-4 w-4" /> {team.name}
                  </CardTitle>
                  <Badge variant="outline">{members.length} members</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {leader && (
                  <div className="flex items-center gap-2 text-sm">
                    <Crown className="h-3.5 w-3.5 text-yellow-500" />
                    <span className="font-medium">{leader.full_name || 'Team Leader'}</span>
                  </div>
                )}
                <div className="space-y-1">
                  {members.map((m: any) => {
                    const p = getProfile(m.user_id);
                    return (
                      <div key={m.id} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold">
                          {(p?.full_name || '?')[0].toUpperCase()}
                        </div>
                        <span>{p?.full_name || 'Unknown'}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
        {teams.length === 0 && <p className="text-muted-foreground col-span-full text-center py-8">No teams created yet</p>}
      </div>
    </div>
  );
}
